import 'react-responsive-carousel/lib/styles/carousel.min.css'

import { css } from '@emotion/react'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import { Avatar, Box, IconButton } from '@mui/material'
import { useEffect, useState } from 'react'
import { Carousel } from 'react-responsive-carousel'

import { useMQ } from '../hooks/useMQ'
import { BlogPost, blogService } from '../services/blog'
import { BlurBox } from './BlurBox'
import { HumaScrollAnimation } from './HumaScrollAnimation'
import { Title } from './Title'
import BackgroundImg from './images/blog-background.png'

export function Blog() {
  const { isXsSize, isMdSize } = useMQ()
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>()
  const [currentIndex, setCurrentIndex] = useState<number>(0)

  const next = () => {
    if (!blogPosts || !blogPosts.length) {
      return
    }

    setCurrentIndex(
      currentIndex === blogPosts.length ? currentIndex : currentIndex + 1,
    )
  }

  const prev = () => {
    setCurrentIndex(currentIndex === 0 ? 0 : currentIndex - 1)
  }

  const currentIndexChanged = (index: number) => {
    setCurrentIndex(index)
  }

  useEffect(() => {
    const getLoanRepaid = async () => {
      try {
        const blogPostsData = await blogService.getBlogPosts()
        setBlogPosts(blogPostsData)
      } catch (e) {
        setBlogPosts([])
      }
    }
    getLoanRepaid()
  }, [])

  if (!blogPosts) {
    return null
  }

  const styles = {
    wrapper: css`
      margin-top: ${isXsSize ? '64px' : '160px'};
      position: relative;
    `,
    carouselWrapper: css`
      height: 550px;
      position: relative;
      z-index: 2;
    `,
    secondaryTitle: css`
      position: relative;
      z-index: 2;
      margin-top: ${isXsSize ? '-20px' : '-30px'};
      margin-bottom: 30px;
      color: #b8b8b8;
      text-align: center;
      font-size: 18px;
      font-weight: 400;
      letter-spacing: 0.15px;
    `,
    blogTitle: css`
      font-weight: 700;
      font-size: ${isXsSize ? '20px' : '24px'};
      margin-top: 10px;
      flex: 1;
      text-align: left;
      color: #cecece;
      line-height: 133.4%;
    `,
    blogDate: css`
      font-size: 14px;
      text-align: left;
      color: #686868;
      font-size: 16px;
      font-weight: 500;
      line-height: 150%;
      letter-spacing: 0.15px;
    `,
    blogPostContentWrapper: css`
      cursor: pointer;
      width: ${isMdSize ? '100%' : '460px'};
      height: ${isXsSize ? '420px' : '490px'};
      display: flex;
      flex-direction: column;
      color: white;
      padding: 20px;
    `,
    blogImage: css`
      width: 100%;
      height: 250px;
      object-fit: cover;
      border-radius: 20px;
      display: block;
    `,
    blogPostsContainer: css`
      z-index: 2;
      position: relative;
      width: 100%;
      max-width: 1200px;
      margin: auto;
    `,
    blogPost: css`
      width: ${isMdSize ? '100%' : 'fit-content'};

      & a {
        text-decoration: none;
      }

      &:hover div {
        background: rgb(30, 30, 30);
      }
    `,
    buttonContainer: css`
      margin-top: 24px;
      display: flex;
      justify-content: center;
      z-index: 2;
      position: relative;
    `,
    blogButton: css`
      width: ${isXsSize ? '40px' : '60px'};
      height: ${isXsSize ? '40px' : '60px'};
      z-index: 2;
      position: relative;
      border-radius: 100px;
      background: #222;
      backdrop-filter: blur(2px);
    `,
    blogButtonIcon: css`
      svg {
        width: ${isXsSize ? '32px' : '56px'};
        height: ${isXsSize ? '32px' : '56px'};
        color: #fff;
      }
      &:hover svg {
        color: #b452ff;
      }
    `,
    blogButtonIconDisabled: css`
      svg {
        color: #6e6c6f;
      }
    `,
    imageOverlay: css`
      position: relative;
      &::before {
        content: '';
        display: block;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
      }
    `,
    backgroundImg: css`
      object-fit: contain;
      position: absolute;
      bottom: ${isXsSize ? '-420px' : '-480px'};
      left: 0;
      width: 100%;
      height: 100%;
    `,
  }

  return (
    <Box css={styles.wrapper}>
      <img
        css={styles.backgroundImg}
        src={BackgroundImg}
        alt='subscription'
      ></img>
      <HumaScrollAnimation>
        <Title title='Huma Blog' />
      </HumaScrollAnimation>
      <HumaScrollAnimation>
        <Box css={styles.secondaryTitle}>
          Read the latest insights, product updates, and stories from the Huma
          team.
        </Box>
      </HumaScrollAnimation>
      <Box css={styles.carouselWrapper}>
        <HumaScrollAnimation>
          <Carousel
            selectedItem={currentIndex}
            onChange={currentIndexChanged}
            centerMode={isMdSize ? false : true}
            centerSlidePercentage={39}
            css={styles.blogPostsContainer}
            showIndicators={false}
            showStatus={false}
            showArrows={false}
            showThumbs={false}
            swipeable={false}
          >
            {blogPosts.map((post) => (
              <Box key={post.title} css={styles.blogPost}>
                <BlurBox fadedBorder>
                  <a
                    css={styles.blogPostContentWrapper}
                    href={post.link}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    <Box css={styles.imageOverlay}>
                      <img
                        src={post.image}
                        css={styles.blogImage}
                        alt={post.title}
                      />
                    </Box>
                    <Box css={styles.blogTitle}>{post.title}</Box>
                    <Box css={styles.blogDate}>{post.postDate}</Box>
                  </a>
                </BlurBox>
              </Box>
            ))}
          </Carousel>
        </HumaScrollAnimation>
        <HumaScrollAnimation>
          <Box css={styles.buttonContainer}>
            <Avatar css={styles.blogButton}>
              <IconButton
                onClick={prev}
                css={[
                  styles.blogButtonIcon,
                  currentIndex === 0 && styles.blogButtonIconDisabled,
                ]}
              >
                <NavigateBeforeIcon />
              </IconButton>
            </Avatar>
            <Avatar sx={{ marginLeft: '50px' }} css={styles.blogButton}>
              <IconButton
                onClick={next}
                css={[
                  styles.blogButtonIcon,
                  currentIndex === blogPosts.length - 1 &&
                    styles.blogButtonIconDisabled,
                ]}
              >
                <NavigateNextIcon />
              </IconButton>
            </Avatar>
          </Box>
        </HumaScrollAnimation>
      </Box>
    </Box>
  )
}
